import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, ShieldCheck } from 'lucide-react';
import divusLogo from '@/assets/divus-immigration-logo.png';

export default function Auth() {
  const { user, signIn, signInWithGoogle, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    const { error, openedInNewTab } = await signInWithGoogle();

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    if (openedInNewTab) {
      setLoading(false);
    }
  };

  const isCorporateDomainError = (msg: string) =>
    msg.toLowerCase().includes('divuslegalgroup') ||
    msg.toLowerCase().includes('divusimmigration') ||
    msg.toLowerCase().includes('corporativo') ||
    msg.toLowerCase().includes('allowed');

  return (
    <div className="relative flex items-center justify-center flex-1 overflow-hidden bg-gradient-to-br from-background via-background to-secondary px-4 py-8">
      {/* Decorative background blobs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-32 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl"
      />
      {/* Subtle dot grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.15]"
        style={{
          backgroundImage:
            'radial-gradient(circle, hsl(var(--foreground)) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Branding */}
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src={divusLogo}
            alt="Divus Immigration"
            className="mb-6 h-16 w-auto object-contain dark:brightness-0 dark:invert"
          />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Bem-vindo de volta
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Acesse sua conta corporativa Divus
          </p>
        </div>

        {/* Card */}
        <div className="rounded-3xl border border-border/60 bg-card/90 p-8 shadow-xl backdrop-blur-sm">
          <div className="space-y-5">
            {error && (
              <Alert variant="destructive" className="rounded-2xl">
                <AlertDescription>
                  {isCorporateDomainError(error)
                    ? 'Apenas emails corporativos (@divuslegalgroup.com ou @divusimmigration.com) podem acessar o sistema.'
                    : error}
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleGoogleSignIn}
              variant="outline"
              size="lg"
              className="w-full h-12 text-sm font-medium border-border hover:bg-secondary/80 hover:border-primary/30 transition-all"
              disabled={loading}
            >
              <svg className="mr-1 h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Continuar com Google'
              )}
            </Button>

            <div className="relative flex items-center py-1">
              <div className="flex-grow border-t border-border" />
              <span className="mx-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Ou
              </span>
              <div className="flex-grow border-t border-border" />
            </div>

            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-1.5">
                <Label
                  htmlFor="signin-email"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Email corporativo
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="signin-email"
                    name="email"
                    type="email"
                    placeholder="seu.nome@divusimmigration.com"
                    autoComplete="email"
                    required
                    className="h-11 pl-11"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="signin-password"
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Senha
                  </Label>
                  <button
                    type="button"
                    className="text-xs font-medium text-primary hover:underline"
                    onClick={() =>
                      setError(
                        'Solicite a redefinição de senha ao administrador.',
                      )
                    }
                  >
                    Esqueceu?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="signin-password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    className="h-11 pl-11"
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full h-12 text-sm font-semibold mt-2"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Entrar'
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>
            Acesso restrito. Solicite seu cadastro ao administrador.
          </span>
        </div>
      </div>
    </div>
  );
}
